// src/composables/useLinkPreviews.js
//
// Estado dos links verificados pelo backend, compartilhado por toda a aplicação.
//
// Ponto central do desenho: a verificação NÃO faz parte do carregamento das notas.
// Os cards renderizam imediatamente com os previews em estado "unknown" (idêntico
// ao comportamento atual); quando o veredito chega, só o mapa reativo muda e os
// previews já montados ganham o selo de quebrado — sem recarregar a lista.
//
// O cache é de módulo (vive enquanto a aba estiver aberta): a mesma URL não é
// verificada de novo ao paginar, filtrar ou trocar de tela.

import { reactive } from "vue";
import { fetchLinkPreviews } from "@/services/linkPreviewService";
import { normalizeUrl } from "@/utils/noteLinks";

// url normalizada → { state: "unknown" | "ok" | "broken", title?, imageUrl? }
const linkStatus = reactive({});

// URLs já enviadas ao backend (evita repetir a mesma verificação em voo).
const requested = new Set();

function getLinkStatus(url) {
  return linkStatus[normalizeUrl(url)];
}

async function ensureLinkPreviews(urls) {
  const pending = [...new Set((urls ?? []).map(normalizeUrl))].filter(
    (url) => url && !requested.has(url)
  );
  if (pending.length === 0) return;

  pending.forEach((url) => requested.add(url));

  const items = await fetchLinkPreviews(pending);

  // Endpoint ausente ou fora do ar: mantém tudo em "unknown" e libera as URLs
  // para uma nova tentativa numa próxima navegação.
  if (!items) {
    pending.forEach((url) => requested.delete(url));
    return;
  }

  const pendingSet = new Set(pending);
  const sameLength = items.length === pending.length;

  items.forEach((item, index) => {
    // Casa pela URL ecoada; se ela não estiver entre as enviadas (backend pode
    // reescrever encoding/host) e as contagens baterem, cai para a posição —
    // o backend devolve na mesma ordem em que enviamos.
    let url = normalizeUrl(item?.url ?? "");
    if (!pendingSet.has(url) && sameLength) {
      url = pending[index];
    }
    if (!url) return;
    linkStatus[url] = {
      state: item?.ok === false ? "broken" : "ok",
      title: item?.title || undefined,
      imageUrl: item?.imageUrl || undefined,
    };
  });
}

export function useLinkPreviews() {
  return { linkStatus, getLinkStatus, ensureLinkPreviews };
}
